import { useEffect, useState } from "react";
import StandingsTable from "../../components/StandingsTable";
import { api } from "../../lib/api";
import { logger } from "../../lib/logger";

/**
 * Group-stage standings tab.
 *
 * Toggleable between:
 *   - "real":      live standings computed from finished group matches
 *                  (delivered via the `/standings` props payload)
 *   - "predicted": tables computed from THIS user's own group-stage picks,
 *                  fetched on-demand from `/pools/{id}/predicted-standings`.
 */
export default function GroupsView({ standings, teams, t, poolId }) {
    const [mode, setMode] = useState("real");
    const [predicted, setPredicted] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (mode !== "predicted" || predicted !== null || !poolId) return;
        let cancelled = false;
        setLoading(true);
        api.get(`/pools/${poolId}/predicted-standings`)
            .then(({ data }) => { if (!cancelled) setPredicted(data || []); })
            .catch((e) => {
                logger.warn("predicted-standings failed", e);
                if (!cancelled) setPredicted([]);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [mode, predicted, poolId]);

    const active = mode === "predicted" ? (predicted || []) : standings;
    const hasReal = standings && standings.length > 0;

    return (
        <div data-testid="groups-view">
            <div className="mb-4 flex items-center gap-2" data-testid="groups-toggle" title={t("groups.view.tooltip")}>
                <button
                    type="button"
                    onClick={() => setMode("real")}
                    aria-pressed={mode === "real"}
                    data-testid="groups-toggle-real"
                    className={`px-3 py-1.5 rounded-md text-xs font-heading font-bold uppercase tracking-widest transition-colors ${
                        mode === "real"
                            ? "bg-[var(--accent-hex)] text-[var(--accent-foreground,#000)]"
                            : "bg-[var(--bg-card)] border border-[var(--border-base)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                    }`}
                >
                    {t("groups.view.real")}
                </button>
                <button
                    type="button"
                    onClick={() => setMode("predicted")}
                    aria-pressed={mode === "predicted"}
                    data-testid="groups-toggle-predicted"
                    className={`px-3 py-1.5 rounded-md text-xs font-heading font-bold uppercase tracking-widest transition-colors ${
                        mode === "predicted"
                            ? "bg-[var(--accent-hex)] text-[var(--accent-foreground,#000)]"
                            : "bg-[var(--bg-card)] border border-[var(--border-base)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                    }`}
                >
                    {t("groups.view.predicted")}
                </button>
            </div>

            {mode === "real" && !hasReal && (
                <div className="text-center py-12 text-[var(--text-secondary)]" data-testid="groups-empty">
                    {t("groups.empty")}
                </div>
            )}
            {mode === "predicted" && loading && (
                <div className="text-center py-12 text-[var(--text-secondary)]" data-testid="groups-predicted-loading">
                    …
                </div>
            )}
            {mode === "predicted" && !loading && active.length === 0 && (
                <div className="text-center py-12 text-[var(--text-secondary)]" data-testid="groups-predicted-empty">
                    {t("groups.predicted.empty")}
                </div>
            )}

            {active.length > 0 && (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid={`groups-grid-${mode}`}>
                    {active.map((s) => (
                        <div key={s.group} className="bg-[var(--bg-card)] border border-[var(--border-base)] rounded-lg p-4" data-testid={`group-table-${s.group}`}>
                            <StandingsTable group={s.group} rows={s.rows} teams={teams} t={t} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
